import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UrlRegex } from '@app/utils/dictionaries/url.assets.dictionary';
import { EMPTY_STRING, ERROR_DEFAULT_MESSAGE, Numbers, ToastMessages } from '@app/constants';
import { ToastService } from '@app/services/toast.service';
import { ToastTypeEnum } from '@app/enums/toast-type.enum';
import { GOAL_ARCHIVED_ERROR_CODE } from '@app/constants/domain/growth-route.constants';
import { GoalArchivedNotifierService } from '@app/services/goal-archived-notifier.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private readonly toastService: ToastService,
    private readonly goalArchivedNotifier: GoalArchivedNotifierService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleHttpError(error);
        return throwError(() => error);
      })
    );
  }

  private handleHttpError(error: HttpErrorResponse): void {
    if (this.isGoalArchivedError(error)) {
      this.handleGoalArchivedError(error);
      return;
    }

    const urlToEvaluate = error.url ?? EMPTY_STRING;
    const allowedUrls: Partial<Record<HttpStatusCode, RegExp[]>> = {
      [HttpStatusCode.BadRequest]: [
        UrlRegex.SCHEDULE_EVENTS_REGEX,
        UrlRegex.GOAL_SCHEDULE_REGEX,
        UrlRegex.SCHEDULE_PUBLISH_MAP_REGEX,
      ],
      [HttpStatusCode.NotFound]: [
        UrlRegex.SCHEDULE_EVENTS_REGEX,
        UrlRegex.CREATE_PRAGMAPOWER_REGEX,
        UrlRegex.SCHEDULE_PUBLISH_MAP_REGEX,
      ],
      [HttpStatusCode.UnprocessableEntity]: [
        UrlRegex.SCHEDULE_EVENTS_REGEX,
        UrlRegex.VALORATION_STATUS_ACTIVE_REGEX,
        UrlRegex.BEHAVIORS_REGEX,
        UrlRegex.IAEVA_TRIGGER_REGEX,
      ],
      [HttpStatusCode.InternalServerError]: [UrlRegex.ASSESSMENT_PA_PRAGMAPOWER_DEFINITIONS_REGEX],
      [HttpStatusCode.Conflict]: [
        UrlRegex.SCHEDULE_EVENTS_REGEX,
        UrlRegex.CREATE_PRAGMAPOWER_REGEX,
        UrlRegex.SCHEDULE_PUBLISH_MAP_REGEX,
      ],
    };
    const isAllowedUrl = (httpStatusCode: HttpStatusCode) =>
      allowedUrls[httpStatusCode]?.some((allowedUrl) => allowedUrl.test(urlToEvaluate)) ?? false;

    if (error.status in allowedUrls && isAllowedUrl(error.status)) {
      return;
    }

    this.displayErrorMessage(error);
  }

  /** `409 GOAL_ARCHIVED`: el objetivo se archivo por cambio de mapa y es de solo lectura. */
  private isGoalArchivedError(error: HttpErrorResponse): boolean {
    return (
      error.status === HttpStatusCode.Conflict && error.error?.error === GOAL_ARCHIVED_ERROR_CODE
    );
  }

  private handleGoalArchivedError(error: HttpErrorResponse): void {
    const message = error.error?.message || ERROR_DEFAULT_MESSAGE;
    this.toastService.open({
      type: ToastTypeEnum.Warning,
      title: ToastMessages.GoalArchivedTitle,
      description: message,
    });
    this.goalArchivedNotifier.notify(message);
  }

  private displayErrorMessage(
    error: HttpErrorResponse,
    type: ToastTypeEnum = ToastTypeEnum.Warning
  ): void {
    let title = `Error Code: ${error.status}`;
    let description = error.error?.userError
      ? JSON.stringify(error.error.userError)
      : ERROR_DEFAULT_MESSAGE;

    if (error.status === Numbers.zero) {
      title = ToastMessages.ConnectionError;
      description = ToastMessages.ConnectionErrorDescription;
    }

    this.toastService.open({
      type,
      title,
      description,
    });
  }
}
